/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  checkBookingAvailability,
  createNewConversation,
  sumbitServiceData,
} from '../service/serviceService';
import { logError } from '../utils/logger';
import {
  bookingTotalCalculation,
  calculateTax,
  completeBookingStatus,
  getTimeSlotRelatedStaff,
  updateBookingStatusToCancel,
} from '../service/bookingService';
import {
  removeUserAddress,
  addUserAddress,
  updateUserAddress,
  updateUserMobile,
} from '../service/userService';
import { reverseGeocode } from '../service/ReverseGeocode';
import { useNavigate } from 'react-router-dom';
import { all_routes } from '../core/data/routes/all_routes';
import { addToast } from '@heroui/react';
import {
  createReview,
  createReviewVote,
  updateReview,
} from '../service/reviewService';
import { useAuth } from '../context/AuthContext';
import {
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '../service/notificationService';
import {
  confirmUserDeactivation,
  requestOtp,
  resendOtp,
} from '../service/accountDeactivationService';
import {
  confirmUserDeletion,
  requestUserDeletion,
} from '../service/accountDeletionService';
import {
  acceptBroadcastQuote,
  acceptJob,
  cancelJob,
  cancelRequestChanges,
  confirmJobCompletion,
  createJob,
  declineJob,
  postThreadMessage,
  requestQuoteChanges,
  uploadThreadAttachments,
  type CreateJobPayload,
} from '../service/jobService';
import type { QuoteThreadAttachment } from '../utils/type';
import {
  acceptPackageOffer,
  bookSessionWithPackage,
  cancelPackage,
  declinePackageOffer,
  purchasePackage,
  toggleAutoRenew,
  type BookSessionPayload,
  type PurchasePackagePayload,
} from '../service/packageService';
import { QueryKey } from './queryKey';

const NOTIFICATION_ROLE = 'customer';

// SERVICE
export const useSumbitServiceMutation = () => {
  return useMutation({
    mutationFn: sumbitServiceData,
    onSuccess: () => {
      localStorage.removeItem('information');
      localStorage.removeItem('availability');
      localStorage.removeItem('location');
      localStorage.removeItem('seo');
    },
    onError: (error: any) => {
      logError('Failed to submit service data:', { error });

      const errorMessage =
        error?.response?.data?.errors ||
        error ||
        'An error occurred while submitting service data.';
      console.log(errorMessage);
    },
  });
};

export const useCheckBookingAvailability = () => {
  return useMutation({
    mutationFn: checkBookingAvailability,
    onSuccess: () => {},
    onError: (error: any) => {
      logError('Failed to submit service data:', { error });

      const errorMessage =
        error?.response?.data?.errors ||
        error ||
        'An error occurred while submitting service data.';
      console.log(errorMessage);
    },
  });
};

//CHECK TIMESLOT AVAILABILITY BY DATE AND SERVICE ID
export const useCheckTimesSlotAvailability = () => {
  return useMutation({
    mutationFn: getTimeSlotRelatedStaff,
    onError: (error: any) => {
      console.error('Failed to check timeslot availability:', error);
    },
  });
};

export const useBookingTotalCalculate = () => {
  return useMutation({
    mutationFn: (data: any) => bookingTotalCalculation(data),
    retry: 3,
    retryDelay: 1000,
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.errors ||
        error ||
        'An error occurred while create review.';
      console.log('Error create review: ', errorMessage);
    },
  });
};

export const useCalculateTax = () => {
  return useMutation({
    mutationFn: (data: any) => calculateTax(data),
    retry: 2,
    retryDelay: 1000,
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.errors ||
        error ||
        'An error occurred while calculating tax.';
      console.log('Error calculate tax: ', errorMessage);
    },
  });
};

// ADD USER ADDRESS
export const useAddUserAddress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      addUserAddress(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['FETCH_USER_BY_ID'],
      });
    },

    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.errors ||
        error ||
        'An error occurred while add user address.';
      console.log('Error add user address: ', errorMessage);
    },
  });
};

export const useReverseGeocodeMutation = () =>
  useMutation({
    mutationFn: ({ lat, lng }: { lat: number; lng: number }) =>
      reverseGeocode(lat, lng),
  });

// UPDATE USER ADDRESS
export const useUpdateUserAddress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
      addressId,
    }: {
      id: string;
      data: any;
      addressId: string;
    }) => updateUserAddress(id, data, addressId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['FETCH_USER_BY_ID'],
      });
    },

    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.errors ||
        error ||
        'An error occurred while update user address.';
      console.log('Error update user address: ', errorMessage);
    },
  });
};

// REMOVE USER ADDRESS
export const useRemoveUserAddress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, addressId }: { id: string; addressId: string }) =>
      removeUserAddress(id, addressId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['FETCH_USER_BY_ID'],
      });
    },

    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.errors ||
        error ||
        'An error occurred while remove user address.';
      console.log('Error remove user address: ', errorMessage);
    },
  });
};

// UPDATE USER MOBILE (post-login phone collection)
export const useUpdateUserMobile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      mobile,
      role,
      email,
    }: {
      userId: string;
      mobile: string;
      role: string;
      email: string;
    }) => updateUserMobile(userId, mobile, role, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['USER_SESSION'] });
    },
  });
};

// create conversation

export const useCreateConversation = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({
      senderId,
      receiverId,
      businessId,
    }: {
      senderId: string;
      receiverId: string;
      businessId: string;
    }) => createNewConversation(senderId, receiverId, businessId),

    onSuccess: () => {
      // Navigate on success
      navigate(all_routes.customerChat);
    },

    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.errors ||
        error?.message ||
        'An error occurred while creating conversation.';
      addToast({
        title: 'Failed to Create Conversation',
        description: errorMessage,
        radius: 'md',
        color: 'danger',
      });
      console.error('Error creating conversation:', errorMessage);
    },
  });
};

// export const useUpdateBookingStatus = () => {
//   return useMutation({
//     mutationFn: ({ id, newStatus }: { id: string; newStatus: string }) =>
//       completeBookingStatus(id, newStatus),
//     onSuccess: () => {
//       getUserBookings();
//       addToast({
//         title: 'Success',
//         description: 'Booking confirm successfully',
//         radius: 'md',
//         color: 'success',
//       });
//       queryClient.invalidateQueries({
//         queryKey: ['get-all-bookings-analytics'],
//       });
//     },
//     onError: () => {
//       addToast({
//         title: 'Error',
//         description: `Failed to change booking status.`,
//         radius: 'md',
//         color: 'danger',
//       });
//     },
//   });
// };
export const useUpdateBookingStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, newStatus }: { id: string; newStatus: string }) =>
      completeBookingStatus(id, newStatus),
    onSuccess: () => {
      addToast({
        title: 'Success',
        description: 'Booking confirm successfully',
        radius: 'md',
        color: 'success',
      });

      // Invalidate both bookings and analytics queries
      queryClient.invalidateQueries({
        queryKey: ['get-all-bookings'],
      });

      queryClient.invalidateQueries({
        queryKey: ['get-all-bookings-analytics'],
      });
    },
    onError: () => {
      addToast({
        title: 'Error',
        description: `Failed to change booking status.`,
        radius: 'md',
        color: 'danger',
      });
    },
  });
};

// export const useUpdateBookingStatusToCancelBooking = () => {
//   return useMutation({
//     mutationFn: ({
//       id,
//       newStatus,
//       note,
//     }: {
//       id: string;
//       newStatus: string;
//       note?: string;
//     }) => updateBookingStatusToCancel(id, newStatus, note),
//     onSuccess: (_data) => {
//       addToast({
//         title: 'Success',
//         description: `Booking cancel successfully.`,
//         radius: 'md',
//         color: 'success',
//       });
//       getUserBookings();
//       queryClient.invalidateQueries({
//         queryKey: ['get-all-bookings-analytics'],
//       });
//     },
//     onError: () => {
//       addToast({
//         title: 'Error',
//         description: `Failed to change booking status.`,
//         radius: 'md',
//         color: 'danger',
//       });
//     },
//   });
// };
export const useUpdateBookingStatusToCancelBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      newStatus,
      note,
    }: {
      id: string;
      newStatus: string;
      note?: string;
    }) => updateBookingStatusToCancel(id, newStatus, note),
    onSuccess: () => {
      addToast({
        title: 'Success',
        description: `Booking cancel successfully.`,
        radius: 'md',
        color: 'success',
      });

      // Invalidate both bookings and analytics queries
      queryClient.invalidateQueries({
        queryKey: ['get-all-bookings'],
      });

      queryClient.invalidateQueries({
        queryKey: ['get-all-bookings-analytics'],
      });
    },
  });
};

export const useUpdateReview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reviewId, data }: { reviewId: string; data: any }) =>
      updateReview(reviewId, data),
    onSuccess: (response, variables) => {
      // Update the specific review in the cache with the response data
      queryClient.setQueryData(
        ['FETCH_REVIEW_BY_ID', variables.reviewId],
        response
      );

      // Invalidate the list queries
      queryClient.invalidateQueries({
        queryKey: ['FETCH_REVIEW_BY_USER_ID'],
      });

      queryClient.invalidateQueries({
        queryKey: ['get-all-bookings'],
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.errors ||
        error ||
        'An error occurred while update review.';
      console.log('Error update review: ', errorMessage);
    },
  });
};

export const useCreateReview = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: any }) => createReview(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['FETCH_REVIEW_BY_SERVICE_ID', id],
      });
      // Invalidate all message queries so the chat component refetches
      // and observes hasCusReviewed=true (clears the review CTA without reload)
      queryClient.invalidateQueries({ queryKey: ['messages'] });

      queryClient.invalidateQueries({
        queryKey: ['get-all-bookings'],
      });
    },

    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.errors ||
        error ||
        'An error occurred while create review.';
      console.log('Error create review: ', errorMessage);
    },
  });
};

export const useCreateReviewVote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      reviewId,
      voteType,
    }: {
      reviewId: string;
      voteType: 'helpful' | 'not_helpful';
    }) => createReviewVote(reviewId, voteType),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['FETCH_REVIEW_BY_SERVICE_ID'],
        exact: false,
      });
    },

    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.errors ||
        error ||
        'An error occurred while create review vote.';
      console.log('Error create review vote: ', errorMessage);
    },
  });
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      if (!user?.userId) {
        throw new Error('User not authenticated');
      }

      return markNotificationAsRead(user.userId, NOTIFICATION_ROLE, [
        notificationId,
      ]);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unreadNotifications'] });

      addToast({
        title: 'Success',
        description: data.message || 'Notification marked as read',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      addToast({
        title: 'Error',
        description: error.message || 'Failed to mark notification as read',
        color: 'danger',
      });
    },
  });
};

export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!user?.userId) {
        throw new Error('User not authenticated');
      }

      return markAllNotificationsAsRead(user.userId, NOTIFICATION_ROLE);
    },
    onSuccess: (data) => {
      // Invalidate and refetch all notification queries
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unreadNotifications'] });

      addToast({
        title: 'Success',
        description: data.message || 'All notifications marked as read',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      addToast({
        title: 'Error',
        description:
          error.message || 'Failed to mark all notifications as read',
        color: 'danger',
      });
    },
  });
};

//Request account deactivation OTP
export const useRequestAccountDeactivationOtp = () => {
  return useMutation({
    mutationFn: ({
      accountType,
      reason,
      feedback,
    }: {
      accountType: string;
      reason: string;
      feedback?: string;
    }) => requestOtp(accountType, reason, feedback),
  });
};

//Verify account deactivation OTP
export const useVerifyAccountDeactivationOtp = () => {
  return useMutation({
    mutationFn: (payload: {
      accountType: string;
      otp: string;
      securityToken: string;
    }) => confirmUserDeactivation(payload),
  });
};

//Resend account delete and deactivate OTP
export const useResendAccountDeactivationAndDeleteOtp = () => {
  return useMutation({
    mutationFn: ({
      accountType,
      action,
    }: {
      accountType: string;
      action: string;
    }) => resendOtp(accountType, action),
    retry: false,
  });
};

//Request account delete OTP
export const useRequestAccountDeleteOtp = () => {
  return useMutation({
    mutationFn: (accountType: string) => requestUserDeletion(accountType),
  });
};

//Verify account delete OTP
export const useVerifyAccountDeleteOtp = () => {
  return useMutation({
    mutationFn: (payload: {
      accountType: string;
      otp: string;
      securityToken: string;
    }) => confirmUserDeletion(payload),
  });
};

// ── Phase 2 — Quote-based booking (Job vertical) ──────────────────────────

/**
 * useCreateJob — POST /v1/job (customer submits a Job Request).
 * On success invalidates the MY_JOBS list so My Job Requests reflects it.
 */
export const useCreateJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateJobPayload) => createJob(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.MY_JOBS] });
    },
    onError: (error: any) => {
      logError('Failed to create job request:', { error });
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to submit job request.';
      addToast({ title: 'Error', description: message, color: 'danger' });
    },
  });
};

/** useAcceptJob — POST /v1/job/:id/accept (customer accepts a quote). */
export const useAcceptJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => acceptJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.MY_JOBS] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.JOB_DETAIL] });
    },
    onError: (error: any) => {
      logError('Failed to accept job:', { error });
      addToast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to accept quote.',
        color: 'danger',
      });
    },
  });
};

/**
 * useAcceptBroadcastQuote — POST /v1/job/broadcast/:batchId/accept (S8
 * comparison view). Accepting one column atomically declines every other
 * sibling on the backend; invalidate the same queries useAcceptJob does so
 * the list/detail views pick up the resolved batch.
 */
export const useAcceptBroadcastQuote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, jobId }: { batchId: string; jobId: string }) =>
      acceptBroadcastQuote(batchId, jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.MY_JOBS] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.JOB_DETAIL] });
    },
    onError: (error: any) => {
      logError('Failed to accept job:', { error });
      addToast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to accept quote.',
        color: 'danger',
      });
    },
  });
};

/** useDeclineJob — POST /v1/job/:id/decline (customer declines a quote). */
export const useDeclineJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { jobId: string; declinedNote?: string }) =>
      declineJob(payload.jobId, payload.declinedNote),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.MY_JOBS] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.JOB_DETAIL] });
    },
    onError: (error: any) => {
      logError('Failed to decline job:', { error });
      addToast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to decline quote.',
        color: 'danger',
      });
    },
  });
};

/**
 * useRequestQuoteChanges — POST /v1/job/:id/request-changes (customer asks
 * the provider to revise the quote; status 'Quoted' -> 'ChangesRequested').
 */
export const useRequestQuoteChanges = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      jobId: string;
      message: string;
      attachments?: QuoteThreadAttachment[];
    }) =>
      requestQuoteChanges(payload.jobId, payload.message, payload.attachments),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.MY_JOBS] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.JOB_DETAIL] });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.JOB_THREAD, variables.jobId],
      });
    },
    onError: (error: any) => {
      logError('Failed to request quote changes:', { error });
      addToast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to request changes.',
        color: 'danger',
      });
    },
  });
};

/**
 * useCancelRequestChanges — POST /v1/job/:id/cancel-request-changes.
 * Reverts a job from 'ChangesRequested' back to 'Quoted', restoring the
 * customer's ability to Accept / Decline / Request Changes on the
 * still-valid quote, and re-invalidates the thread since the negotiation
 * lock state changes with the status.
 */
export const useCancelRequestChanges = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => cancelRequestChanges(jobId),
    onSuccess: (_data, jobId) => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.MY_JOBS] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.JOB_DETAIL] });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.JOB_THREAD, jobId],
      });
    },
    onError: (error: any) => {
      logError('Failed to cancel change request:', { error });
      addToast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to cancel the change request.',
        color: 'danger',
      });
    },
  });
};

/** usePostThreadMessage — POST /v1/job/:id/thread/messages. */
export const usePostThreadMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      jobId: string;
      body?: string;
      attachments?: QuoteThreadAttachment[];
      revisionNo?: number;
    }) => postThreadMessage(payload.jobId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.JOB_THREAD, variables.jobId],
      });
    },
    onError: (error: any) => {
      logError('Failed to post thread message:', { error });
      addToast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to send message.',
        color: 'danger',
      });
    },
  });
};

/**
 * useUploadThreadAttachment — POST /v1/job/:id/thread/attachments
 * (multipart). Called before `usePostThreadMessage`, per the two-step
 * upload-then-attach flow — the returned metadata is passed into the
 * following `postThreadMessage`/`requestQuoteChanges` call.
 */
export const useUploadThreadAttachment = () => {
  return useMutation({
    mutationFn: (payload: { jobId: string; files: File[] }) =>
      uploadThreadAttachments(payload.jobId, payload.files),
    onError: (error: any) => {
      logError('Failed to upload thread attachment:', { error });
      addToast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to upload attachment.',
        color: 'danger',
      });
    },
  });
};

/** useCancelJob — POST /v1/job/:id/cancel (customer cancels a quote job). */
export const useCancelJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      jobId,
      cancelledNote,
    }: {
      jobId: string;
      cancelledNote?: string;
    }) => cancelJob(jobId, cancelledNote),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.MY_JOBS] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.JOB_DETAIL] });
    },
    onError: (error: any) => {
      logError('Failed to cancel job:', { error });
      addToast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to cancel job.',
        color: 'danger',
      });
    },
  });
};

/**
 * useConfirmJobCompletion — POST /v1/job/:id/confirm-completion (customer
 * confirms a provider-completed job).
 */
export const useConfirmJobCompletion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => confirmJobCompletion(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.MY_JOBS] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.JOB_DETAIL] });
    },
    onError: (error: any) => {
      logError('Failed to confirm job completion:', { error });
      addToast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to confirm completion.',
        color: 'danger',
      });
    },
  });
};

// ── Phase 3 — Package-based booking (CustomerPackage vertical) ─────────────

/**
 * usePurchasePackage — POST /v1/customer-package/purchase.
 *
 * Creates a CustomerPackage with `paymentStatus: 'PENDING'`; this call alone
 * does NOT call gigmosaic-payment-service (see packageService.purchasePackage's
 * doc comment). The caller (PackageCheckout.tsx) drives the actual payment —
 * online via PaymentSection, or offline via Cash on Hand.
 */
export const usePurchasePackage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PurchasePackagePayload) => purchasePackage(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.MY_CUSTOMER_PACKAGES],
      });
    },
    onError: (error: any) => {
      logError('Failed to purchase package:', { error });
      addToast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to purchase package.',
        color: 'danger',
      });
    },
  });
};

/** useToggleAutoRenew — POST /v1/customer-package/:id/auto-renew. */
export const useToggleAutoRenew = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerPackageId,
      autoRenew,
    }: {
      customerPackageId: string;
      autoRenew: boolean;
    }) => toggleAutoRenew(customerPackageId, autoRenew),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.MY_CUSTOMER_PACKAGES],
      });
    },
    onError: (error: any) => {
      logError('Failed to toggle auto-renew:', { error });
      addToast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to update auto-renew.',
        color: 'danger',
      });
    },
  });
};

/**
 * useAcceptPackageOffer — POST /v1/customer-package/:id/accept (customer
 * accepts a provider-initiated package offer → offerStatus 'ACTIVE').
 */
export const useAcceptPackageOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (customerPackageId: string) =>
      acceptPackageOffer(customerPackageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.MY_CUSTOMER_PACKAGES],
      });
    },
    onError: (error: any) => {
      logError('Failed to accept package offer:', { error });
      addToast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to accept package offer.',
        color: 'danger',
      });
    },
  });
};

/**
 * useDeclinePackageOffer — POST /v1/customer-package/:id/decline (customer
 * declines a provider-initiated package offer → offerStatus 'DECLINED').
 */
export const useDeclinePackageOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (customerPackageId: string) =>
      declinePackageOffer(customerPackageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.MY_CUSTOMER_PACKAGES],
      });
    },
    onError: (error: any) => {
      logError('Failed to decline package offer:', { error });
      addToast({
        title: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to decline package offer.',
        color: 'danger',
      });
    },
  });
};

/**
 * useCancelPackage — POST /v1/customer-package/:id/cancel (customer cancels
 * an active package → offerStatus 'CANCELLED'). Remaining sessions are forfeited.
 */
export const useCancelPackage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerPackageId,
      cancelledReason,
    }: {
      customerPackageId: string;
      cancelledReason?: string;
    }) => cancelPackage(customerPackageId, cancelledReason),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.MY_CUSTOMER_PACKAGES],
      });
      addToast({
        title: 'Success',
        description:
          'Package cancelled. Remaining sessions have been forfeited.',
        color: 'success',
      });
    },
    onError: (error: any) => {
      logError('Failed to cancel package:', { error });

      // Check for the specific "session still active" 409 error
      const errorMessage = error?.response?.data?.message || '';
      if (
        error?.response?.status === 409 &&
        errorMessage.includes('session') &&
        (errorMessage.includes('still scheduled') ||
          errorMessage.includes('still confirmed'))
      ) {
        addToast({
          title: 'Cannot Cancel Package',
          description:
            'This package has a session that is still scheduled or confirmed. Please cancel that booking first.',
          color: 'warning',
        });
      } else {
        addToast({
          title: 'Error',
          description:
            error?.response?.data?.message ||
            error?.message ||
            'Failed to cancel package.',
          color: 'danger',
        });
      }
    },
  });
};

/**
 * useBookSession — POST /v1/customer-package/:id/book-session.
 * Redeems ONE credit (no payment for the credit itself). Resolves to
 * `{ customerPackage, booking }` — the caller (CreditSession.tsx) checks
 * `booking.addonSubtotal` to decide whether a post-booking add-on payment
 * step is needed (Phase 4).
 */
export const useBookSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerPackageId,
      data,
    }: {
      customerPackageId: string;
      data: BookSessionPayload;
    }) => bookSessionWithPackage(customerPackageId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.MY_CUSTOMER_PACKAGES],
      });
    },
    onError: (error: any) => {
      logError('Failed to book session:', { error });

      // Handle 409 (duplicate booking) error specifically
      if (error?.response?.status === 409) {
        addToast({
          title: 'Booking Conflict',
          description:
            'This time slot has already been booked or is no longer available. Please select a different time.',
          color: 'warning',
        });
      } else {
        addToast({
          title: 'Error',
          description:
            error?.response?.data?.message ||
            error?.message ||
            'Failed to book session.',
          color: 'danger',
        });
      }
    },
  });
};
